/**
 * Iter57g — Visual progression stepper for vacation / WFH requests.
 *
 * Shows 4 stages of the approval lifecycle:
 *   1. Predan (created)
 *   2. Voditelj (manager_decided_at)   — branches to ✗ if rejected
 *   3. Tajništvo (pdf_generated_at)    — PDF generated
 *   4. Poslan (sent_at)                — email delivered
 *
 * Compact horizontal layout — single line on desktop, wraps on mobile.
 * Status determines which steps are completed / active / pending / failed.
 */
import { Check, X, Send, FileText, UserCheck, FileSignature } from "lucide-react";

const STAGE_DEFS = [
  { key: "submitted", label: "Predan",     icon: FileSignature },
  { key: "manager",   label: "Voditelj",   icon: UserCheck },
  { key: "office",    label: "Tajništvo",  icon: FileText },
  { key: "sent",      label: "Poslan",     icon: Send },
];

/**
 * Compute per-stage state from the request document.
 * Returns array of { state: "done" | "active" | "pending" | "failed", ts?: string }
 */
function computeStages(req) {
  const status = req.status || "pending";
  // Step 1 — always done when request exists
  const submitted = { state: "done", ts: req.created_at };

  // Step 2 — Manager decision
  let manager;
  if (status === "rejected") {
    manager = { state: "failed", ts: req.manager_decided_at };
  } else if (req.manager_decided_at || ["approved", "decided", "sent"].includes(status)) {
    manager = { state: "done", ts: req.manager_decided_at };
  } else {
    manager = { state: "active" };
  }

  // Step 3 — Office (PDF generated)
  let office;
  if (status === "rejected") {
    office = { state: "pending" };  // never reached
  } else if (req.pdf_generated_at || ["decided", "sent"].includes(status)) {
    office = { state: "done", ts: req.pdf_generated_at };
  } else if (status === "approved") {
    office = { state: "active" };
  } else {
    office = { state: "pending" };
  }

  // Step 4 — Sent
  let sent;
  if (status === "rejected") {
    sent = { state: "pending" };
  } else if (req.sent_at || status === "sent") {
    sent = { state: "done", ts: req.sent_at };
  } else if (status === "decided") {
    sent = { state: "active" };
  } else {
    sent = { state: "pending" };
  }

  return [submitted, manager, office, sent];
}

function stateClasses(state) {
  switch (state) {
    case "done":
      return { circle: "bg-emerald-500 text-white border-emerald-500",
               line: "bg-emerald-500", label: "text-emerald-700" };
    case "active":
      return { circle: "bg-primary text-primary-foreground border-primary animate-pulse",
               line: "bg-muted", label: "text-primary font-medium" };
    case "failed":
      return { circle: "bg-red-500 text-white border-red-500",
               line: "bg-muted", label: "text-red-700 font-medium" };
    default: // pending
      return { circle: "bg-muted text-muted-foreground border-muted-foreground/30",
               line: "bg-muted", label: "text-muted-foreground" };
  }
}

export function RequestStatusStepper({ request, className = "" }) {
  if (!request) return null;
  const stages = computeStages(request);

  return (
    <div className={`w-full ${className}`} data-testid="request-stepper">
      <div className="flex items-center justify-between gap-1 sm:gap-2">
        {STAGE_DEFS.map((def, idx) => {
          const s = stages[idx];
          const cls = stateClasses(s.state);
          const isLast = idx === STAGE_DEFS.length - 1;
          const Icon = def.icon;
          return (
            <div key={def.key} className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0"
                 data-testid={`stepper-step-${def.key}`}>
              <div className="flex flex-col items-center min-w-0">
                <div
                  className={`relative w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${cls.circle}`}
                  title={def.label + (s.ts ? ` (${new Date(s.ts).toLocaleString("hr-HR", { dateStyle: "short", timeStyle: "short" })})` : "")}
                  data-testid={`stepper-icon-${def.key}`}
                  data-state={s.state}
                >
                  {s.state === "done" && <Check className="w-4 h-4" />}
                  {s.state === "failed" && <X className="w-4 h-4" />}
                  {(s.state === "active" || s.state === "pending") && <Icon className="w-3.5 h-3.5" />}
                </div>
                <div className={`text-[10px] sm:text-xs mt-1 text-center truncate max-w-[60px] sm:max-w-none ${cls.label}`}>
                  {def.label}
                </div>
              </div>
              {!isLast && (
                <div className={`h-0.5 flex-1 min-w-[8px] -translate-y-2.5 ${cls.line} transition-colors`}
                     aria-hidden="true"/>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RequestStatusStepper;
